import { useState, useCallback, useMemo } from 'react';

/**
 * useTableFilter - Reusable hook for Excel-like table filtering
 * 
 * Uses an ADDITIVE model:
 * - Empty selectedValues = no filter (show all rows)
 * - When you check values, you're choosing WHICH values to show
 * - Supports text filters, numeric comparisons, and date ranges
 * 
 * @param {Object} options
 * @param {Array} options.columns - Column definitions with { key, label, type }
 * @param {Array} options.items - Data items to filter
 * @returns {Object} Filter state and actions
 */
const useTableFilter = ({ columns = [], items = [] }) => {
  // Filter state - using additive model (selectedValues = values to INCLUDE)
  const [selectedValues, setSelectedValues] = useState({});   // { columnKey: ['val1', 'val2'] } - checkbox selections
  const [textFilters, setTextFilters] = useState({});         // { columnKey: { mode: 'contains', text: 'search' } }
  const [numericFilters, setNumericFilters] = useState({});   // { columnKey: { operator: 'gt', value: 10, value2?: 20 } }
  const [dateFilters, setDateFilters] = useState({});         // { columnKey: { operator: 'after', date: '2024-01-01', date2?: '2024-12-31' } }
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');       // Search within filter dropdown

  /**
   * Get unique values for a column (for filter dropdown checkboxes)
   */
  const getUniqueValues = useCallback((columnKey) => {
    const values = new Set();
    let hasBlank = false;
    items.forEach(item => {
      const val = item[columnKey];
      if (val === null || val === undefined || val === '') {
        hasBlank = true;
      } else {
        values.add(String(val));
      }
    });
    const sorted = Array.from(values).sort((a, b) => {
      // Try numeric sort first
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return a.localeCompare(b);
    });
    if (hasBlank) sorted.push('(Blank)');
    return sorted;
  }, [items]);

  /**
   * Toggle a single value in the filter (check/uncheck a checkbox)
   */
  const toggleValue = useCallback((columnKey, value) => {
    setSelectedValues(prev => {
      const current = prev[columnKey] || [];
      const newValues = current.includes(value)
        ? current.filter(v => v !== value)  // Uncheck: remove value
        : [...current, value];              // Check: add value
      
      // If no values selected, remove the filter entirely
      if (newValues.length === 0) {
        const { [columnKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [columnKey]: newValues };
    });
    // Clear text/numeric/date filters for this column when using checkboxes
    setTextFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setNumericFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setDateFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
  }, []);

  /**
   * Select all values for a column — checks every box. Equivalent to no filter
   * for row visibility (every value is included).
   */
  const selectAll = useCallback((columnKey, allValues) => {
    const values = Array.isArray(allValues) ? [...allValues] : [];
    setSelectedValues((prev) => {
      // Same as "no filter" when everything is selected — drop the key.
      if (values.length === 0) {
        const { [columnKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [columnKey]: values };
    });
    setTextFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setNumericFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setDateFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
  }, []);

  /**
   * Clear checkbox selection for a column. Uses an explicit [] so the dropdown
   * unchecks every box (deleting the key looked identical to "no filter" and
   * left boxes looking selected in Excel-style UIs). Row filter is still off
   * while the list is empty (additive model: empty = show all).
   */
  const clearColumn = useCallback((columnKey) => {
    setSelectedValues((prev) => ({ ...prev, [columnKey]: [] }));
    setTextFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setNumericFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setDateFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterSearch('');
  }, []);

  /**
   * Apply text-based filter with operator
   */
  const applyTextFilter = useCallback((columnKey, mode, text) => {
    if (!text.trim() && mode !== 'empty' && mode !== 'notEmpty') {
      // Clear text filter if no text
      setTextFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    } else {
      // Apply text filter (clears checkbox selection for this column)
      setTextFilters(prev => ({ ...prev, [columnKey]: { mode, text } }));
      setSelectedValues(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setNumericFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setDateFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    }
    setOpenFilterColumn(null);
    setFilterSearch('');
  }, []);

  /**
   * Apply numeric filter with operator
   */
  const applyNumericFilter = useCallback((columnKey, operator, value, value2 = null) => {
    if (value === null || value === undefined || value === '') {
      // Clear numeric filter if no value
      setNumericFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    } else {
      // Apply numeric filter
      setNumericFilters(prev => ({ ...prev, [columnKey]: { operator, value: Number(value), value2: value2 !== null ? Number(value2) : null } }));
      setSelectedValues(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setTextFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setDateFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    }
    setOpenFilterColumn(null);
    setFilterSearch('');
  }, []);

  /**
   * Apply date filter with operator
   */
  const applyDateFilter = useCallback((columnKey, operator, date, date2 = null) => {
    if (!date && operator !== 'empty' && operator !== 'notEmpty') {
      // Clear date filter if no date
      setDateFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    } else {
      // Apply date filter
      setDateFilters(prev => ({ ...prev, [columnKey]: { operator, date, date2 } }));
      setSelectedValues(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setTextFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setNumericFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    }
    setOpenFilterColumn(null);
    setFilterSearch('');
  }, []);

  /**
   * Clear all filters
   */
  const clearAllFilters = useCallback(() => {
    setSelectedValues({});
    setTextFilters({});
    setNumericFilters({});
    setDateFilters({});
    setFilterSearch('');
    setOpenFilterColumn(null);
  }, []);

  /**
   * Check if column has active filter
   */
  const isPartialValueFilter = useCallback((columnKey, values) => {
    if (!Array.isArray(values) || values.length === 0) return false;
    const all = getUniqueValues(columnKey);
    // Select All checks every box — treat as no filter for the funnel / chips.
    if (all.length > 0 && values.length >= all.length && all.every((v) => values.includes(v))) {
      return false;
    }
    return true;
  }, [getUniqueValues]);

  const hasColumnFilter = useCallback((columnKey) => {
    // [] is an explicit clear — not an active filter.
    return !!(
      isPartialValueFilter(columnKey, selectedValues[columnKey]) ||
      textFilters[columnKey] ||
      numericFilters[columnKey] ||
      dateFilters[columnKey]
    );
  }, [selectedValues, textFilters, numericFilters, dateFilters, isPartialValueFilter]);

  /**
   * Get active filters list for display
   */
  const activeFilters = useMemo(() => {
    const active = [];
    
    // Checkbox-based filters (skip Select-All / full selections)
    Object.entries(selectedValues).forEach(([key, values]) => {
      if (isPartialValueFilter(key, values)) {
        const col = columns.find(c => c.key === key);
        active.push({ key, label: col?.label || key, type: 'values', count: values.length, values });
      }
    });
    
    // Text filters
    Object.entries(textFilters).forEach(([key, filter]) => {
      if (filter) {
        const col = columns.find(c => c.key === key);
        active.push({ key, label: col?.label || key, type: 'text', mode: filter.mode, value: filter.text });
      }
    });
    
    // Numeric filters
    Object.entries(numericFilters).forEach(([key, filter]) => {
      if (filter) {
        const col = columns.find(c => c.key === key);
        let description = `${filter.operator} ${filter.value}`;
        if (filter.operator === 'between' && filter.value2 !== null) {
          description = `${filter.value} - ${filter.value2}`;
        }
        active.push({ key, label: col?.label || key, type: 'numeric', operator: filter.operator, value: filter.value, value2: filter.value2, description });
      }
    });
    
    // Date filters
    Object.entries(dateFilters).forEach(([key, filter]) => {
      if (filter) {
        const col = columns.find(c => c.key === key);
        let description = `${filter.operator} ${filter.date || ''}`;
        if (filter.operator === 'between' && filter.date2) {
          description = `${filter.date} to ${filter.date2}`;
        }
        active.push({ key, label: col?.label || key, type: 'date', operator: filter.operator, date: filter.date, date2: filter.date2, description });
      }
    });
    
    return active;
  }, [selectedValues, textFilters, numericFilters, dateFilters, columns, isPartialValueFilter]);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = activeFilters.length > 0;

  /**
   * Apply all filters to items
   * @param {Array} inputItems - Items to filter (allows chaining with search)
   * @returns {Array} Filtered items
   */
  const applyFilters = useCallback((inputItems) => {
    let result = [...inputItems];

    // Apply checkbox-based filters (selectedValues = values to INCLUDE)
    Object.entries(selectedValues).forEach(([columnKey, values]) => {
      if (!isPartialValueFilter(columnKey, values)) return;
      result = result.filter(item => {
        const val = item[columnKey];
        const isBlank = val === null || val === undefined || val === '';
        if (isBlank) return values.includes('(Blank)');
        return values.includes(String(val));
      });
    });

    // Apply text-based filters
    Object.entries(textFilters).forEach(([columnKey, filter]) => {
      if (!filter) return;
      const { mode, text } = filter;
      const searchText = text?.toLowerCase() || '';
      
      result = result.filter(item => {
        const val = item[columnKey];
        const strVal = val !== null && val !== undefined ? String(val).toLowerCase() : '';
        
        switch (mode) {
          case 'contains': return strVal.includes(searchText);
          case 'notContains': return !strVal.includes(searchText);
          case 'equals': return strVal === searchText;
          case 'notEquals': return strVal !== searchText;
          case 'startsWith': return strVal.startsWith(searchText);
          case 'endsWith': return strVal.endsWith(searchText);
          case 'empty': return strVal === '';
          case 'notEmpty': return strVal !== '';
          default: return true;
        }
      });
    });

    // Apply numeric filters
    Object.entries(numericFilters).forEach(([columnKey, filter]) => {
      if (!filter) return;
      const { operator, value, value2 } = filter;
      
      result = result.filter(item => {
        const val = item[columnKey];
        if (val === null || val === undefined || val === '') {
          return operator === 'empty';
        }
        const numVal = Number(val);
        if (isNaN(numVal)) return false;
        
        switch (operator) {
          case 'eq': return numVal === value;
          case 'neq': return numVal !== value;
          case 'gt': return numVal > value;
          case 'gte': return numVal >= value;
          case 'lt': return numVal < value;
          case 'lte': return numVal <= value;
          case 'between': return numVal >= value && numVal <= (value2 || value);
          case 'empty': return false; // Already handled above
          case 'notEmpty': return true;
          default: return true;
        }
      });
    });

    // Apply date filters
    Object.entries(dateFilters).forEach(([columnKey, filter]) => {
      if (!filter) return;
      const { operator, date, date2 } = filter;
      
      result = result.filter(item => {
        const val = item[columnKey];
        if (val === null || val === undefined || val === '') {
          return operator === 'empty';
        }
        
        const itemDate = new Date(val);
        if (isNaN(itemDate.getTime())) return false;
        
        const filterDate = date ? new Date(date) : null;
        const filterDate2 = date2 ? new Date(date2) : null;
        
        switch (operator) {
          case 'eq': return filterDate && itemDate.toDateString() === filterDate.toDateString();
          case 'before': return filterDate && itemDate < filterDate;
          case 'after': return filterDate && itemDate > filterDate;
          case 'onOrBefore': return filterDate && itemDate <= filterDate;
          case 'onOrAfter': return filterDate && itemDate >= filterDate;
          case 'between': return filterDate && filterDate2 && itemDate >= filterDate && itemDate <= filterDate2;
          case 'empty': return false; // Already handled above
          case 'notEmpty': return true;
          default: return true;
        }
      });
    });

    return result;
  }, [selectedValues, textFilters, numericFilters, dateFilters, isPartialValueFilter]);

  /**
   * Get column type (for determining which filter UI to show)
   */
  const getColumnType = useCallback((columnKey) => {
    const col = columns.find(c => c.key === columnKey);
    return col?.type || 'text';
  }, [columns]);

  return {
    // State
    selectedValues,
    textFilters,
    numericFilters,
    dateFilters,
    openFilterColumn,
    filterSearch,
    activeFilters,
    hasActiveFilters,

    // Actions
    toggleValue,
    selectAll,
    clearColumn,
    applyTextFilter,
    applyNumericFilter,
    applyDateFilter,
    clearAllFilters,
    setOpenFilterColumn,
    setFilterSearch,
    hasColumnFilter,
    getUniqueValues,
    getColumnType,

    // Filter function
    applyFilters,
  };
};

export default useTableFilter;
